import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useRevalidator } from "react-router-dom";
import { toast } from "sonner";

import { switchTeamAction } from "@/features/teams/actions";
import { CreateTeamDialog } from "@/features/teams/create-team-dialog";
import { cn } from "@/lib/utils";
import { useAuthState } from "@/src/lib/auth";
import { invalidateAuthCaches, primeActiveTeamCaches } from "@/src/lib/loaders";

export interface TeamSwitcherTeam {
  team_id: string;
  team_slug: string;
  team_name: string;
  org_id: string;
  org_name: string;
  org_plan: string;
  role: string;
  is_active: boolean;
}

interface Props {
  teams: TeamSwitcherTeam[];
  /**
   * Active team_id. We trust the server-supplied list's `is_active` flag, but
   * pass this explicitly so the trigger can render the active name even
   * before the dropdown opens.
   */
  activeTeamId: string;
  activeTeamName: string;
}

/**
 * Workspace switcher that lives in `AppHeader`.
 *
 * Click → drop down a list of every team the user belongs to (each with a
 * checkmark on the active row, plus a tiny role pill). Selecting a different
 * team calls `switchTeamAction`, which POSTs `/v1/me/active-team` on the
 * gateway. On success the gateway re-issues the `kos_session` cookie scoped
 * to the new team; we then call `revalidator.revalidate()` so every active
 * loader re-runs against the new tenant — the SPA stays mounted, no full
 * page reload.
 *
 * The "Create new team..." footer opens a dialog that POSTs `/v1/teams`
 * (same rotation contract → user is auto-switched into the new team).
 */
export function TeamSwitcher({ teams, activeTeamId, activeTeamName }: Props) {
  const [pending, setPending] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const revalidator = useRevalidator();
  const auth = useAuthState();
  const revalidating = revalidator.state !== "idle";

  const handleSelect = async (teamId: string): Promise<void> => {
    if (teamId === activeTeamId) return;
    setPending(true);
    try {
      const result = await switchTeamAction(teamId);
      if (result.ok) {
        toast.success(result.message);
        const selected = teams.find((team) => team.team_id === teamId);
        if (selected) {
          // Prime `/me` + `/me/teams` before revalidating so the
          // dashboard chrome switches workspaces immediately. The
          // loader still re-fetches in the background and corrects any
          // server-side mismatch.
          primeActiveTeamCaches(auth, teams, { ...selected, is_active: true });
        } else {
          invalidateAuthCaches();
        }
        revalidator.revalidate();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label="Switch team"
            disabled={pending || revalidating}
            className={cn(
              "inline-flex h-7 max-w-[200px] items-center gap-1.5 rounded-md border border-border/70 bg-card px-2 text-[12px] font-medium text-foreground shadow-sm transition",
              "hover:bg-muted disabled:cursor-progress disabled:opacity-60",
            )}
          >
            {pending || revalidating ? (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
            ) : null}
            <span className="truncate">{activeTeamName}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className={cn(
              "z-50 min-w-[260px] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md",
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            )}
          >
            <DropdownMenu.Label className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
              Workspaces
            </DropdownMenu.Label>
            <DropdownMenu.Group>
              {teams.length === 0 ? (
                <div className="px-3 py-2 text-[12px] text-muted-foreground">No teams yet</div>
              ) : (
                teams.map((t) => (
                  <DropdownMenu.Item
                    key={t.team_id}
                    onSelect={(e) => {
                      e.preventDefault();
                      void handleSelect(t.team_id);
                    }}
                    className={cn(
                      "flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-[13px] outline-none",
                      "focus:bg-accent focus:text-accent-foreground",
                    )}
                  >
                    <span className="mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                      {t.is_active ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium leading-tight">
                        {t.team_name}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {t.org_name} · {humanizeRole(t.role)}
                      </span>
                    </span>
                  </DropdownMenu.Item>
                ))
              )}
            </DropdownMenu.Group>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Item
              onSelect={(e) => {
                e.preventDefault();
                setCreateOpen(true);
              }}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-[13px] outline-none focus:bg-accent focus:text-accent-foreground"
            >
              <Plus className="h-3.5 w-3.5 text-muted-foreground" />
              Create new team…
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <CreateTeamDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(result) => {
          setCreateOpen(false);
          const tenant = result.rotation?.tenant;
          if (tenant) {
            primeActiveTeamCaches(auth, teams, {
              team_id: tenant.team_id,
              team_slug: tenant.team_slug,
              team_name: tenant.team_name,
              org_id: tenant.org_id,
              org_name: tenant.org_name,
              org_plan: tenant.org_plan ?? auth.tenant.org_plan,
              role: tenant.role,
              is_active: true,
            });
          } else {
            invalidateAuthCaches();
          }
          revalidator.revalidate();
        }}
      />
    </>
  );
}

function humanizeRole(role: string): string {
  if (role === "team_admin") return "Admin";
  if (role === "team_editor") return "Editor";
  if (role === "team_viewer") return "Viewer";
  return role;
}
